import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["prompt", "input", "error"];

  connect() {
    this.form = this.element.closest("form");
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onPackageSelected = this.onPackageSelected.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.element.addEventListener("click", this.onClick);
    this.element.addEventListener("change", this.onChange);
    this.element.addEventListener("packageSelected", this.onPackageSelected);

    if (this.form) {
      this.form.addEventListener("submit", this.onSubmit, true);
    }

    this.update();
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick);
    this.element.removeEventListener("change", this.onChange);
    this.element.removeEventListener("packageSelected", this.onPackageSelected);

    if (this.form) {
      this.form.removeEventListener("submit", this.onSubmit, true);
    }
  }

  syncHighSchool(event) {
    this.syncHighSchoolInputs(event.currentTarget.value, event.currentTarget);
    this.clearError();
  }

  onClick(event) {
    if (event.target.closest("a, button, input, label")) {
      return;
    }

    const panel = event.target.closest("[data-package-id][data-requires-high-school-on-enrollment]");
    if (!panel || !this.element.contains(panel)) {
      return;
    }

    const radio = panel.querySelector('input[type="radio"][name="selected_package"]');
    if (!radio || radio.checked) {
      return;
    }

    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }

  onChange(event) {
    if (event.target.name === "selected_package") {
      this.update();
      return;
    }

    if (event.target.name === "driving_school_booking[high_school]") {
      this.syncHighSchoolInputs(event.target.value, event.target);
      this.clearError();
    }
  }

  onPackageSelected() {
    this.update();
  }

  onSubmit(event) {
    if (!this.selectedPackageRequiresHighSchool()) {
      return;
    }

    const highSchool = this.highSchoolValue().trim();
    if (highSchool !== "") {
      this.syncHighSchoolInputs(highSchool);
      this.clearError();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showPrompt();
    this.showError();
    this.inputTarget.focus();
    this.inputTarget.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  update() {
    this.syncSelectedPackageId();
    this.syncPackageHighlight();

    if (this.selectedPackageRequiresHighSchool()) {
      this.showPrompt();
      this.prefillPromptFromExistingHighSchool();
    } else {
      this.hidePrompt();
    }
  }

  selectedPackagePanel() {
    const checkedRadio = this.element.querySelector('input[type="radio"][name="selected_package"]:checked');
    if (checkedRadio) {
      return checkedRadio.closest("[data-requires-high-school-on-enrollment]");
    }

    const selectedPackageId = this.element.querySelector('input[name="selected_package_id"]')?.value;
    if (!selectedPackageId) {
      return null;
    }

    return Array.from(
      this.element.querySelectorAll("[data-package-id][data-requires-high-school-on-enrollment]")
    ).find(panel => panel.dataset.packageId === selectedPackageId) || null;
  }

  selectedPackageRequiresHighSchool() {
    return this.selectedPackagePanel()?.dataset.requiresHighSchoolOnEnrollment === "true";
  }

  showPrompt() {
    this.promptTarget.hidden = false;
    this.inputTarget.disabled = false;
    this.inputTarget.required = true;
    this.inputTarget.setAttribute("aria-required", "true");
  }

  hidePrompt() {
    this.promptTarget.hidden = true;
    this.inputTarget.disabled = true;
    this.inputTarget.required = false;
    this.inputTarget.setAttribute("aria-required", "false");
    this.clearError();
  }

  showError() {
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.hidden = true;
    }
  }

  prefillPromptFromExistingHighSchool() {
    const highSchool = this.highSchoolValue().trim();
    if (highSchool !== "" && this.inputTarget.value.trim() === "") {
      this.inputTarget.value = highSchool;
    }
  }

  highSchoolInputs() {
    const scope = this.form || document;
    return Array.from(scope.querySelectorAll('input[name="driving_school_booking[high_school]"]'));
  }

  highSchoolValue() {
    const ownValue = this.hasInputTarget ? this.inputTarget.value : "";
    if (ownValue.trim() !== "") {
      return ownValue;
    }

    const populatedInput = this.highSchoolInputs().find(input => input.value.trim() !== "");
    return populatedInput ? populatedInput.value : ownValue;
  }

  syncHighSchoolInputs(value, sourceInput = null) {
    this.highSchoolInputs().forEach(input => {
      if (input !== sourceInput) {
        input.value = value;
      }
    });
  }

  syncSelectedPackageId() {
    const checkedRadio = this.element.querySelector('input[type="radio"][name="selected_package"]:checked');
    const selectedPackageIdField = this.element.querySelector('input[name="selected_package_id"]');
    if (checkedRadio && selectedPackageIdField) {
      selectedPackageIdField.value = checkedRadio.value;
    }
  }

  syncPackageHighlight() {
    this.element.querySelectorAll("[data-requires-high-school-on-enrollment]").forEach(panel => {
      const radio = panel.querySelector('input[type="radio"][name="selected_package"]');
      if (!radio) {
        return;
      }

      if (radio.checked) {
        panel.classList.remove("Panel--subtle");
      } else if (!panel.classList.contains("Panel--success")) {
        panel.classList.add("Panel--subtle");
      }
    });
  }
}
